import { useCallback } from 'react'
import { useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query'
import type { AgentRow } from '../transport/api'
import {
  addPin,
  BffError,
  getPins,
  placePin,
  removePin,
  removePinTag,
  renamePinTag,
  reorderPin,
  type PinsAnswer,
} from '../transport/relay'
import { rankBetween, renumber, sortPins } from './pinOrder'

/**
 * PINNED AGENTS — owner, 2026-08-24: 在 side panel 每一個 Agent 的右邊增加一個小按鈕,
 * 可以用來將這個 Agent pin 到 side panel 的上方…這樣就可以很方便地快速讀取我們最關心
 * 的 Agent 的狀態. Then: 讓它成為 Global state — a pin set on the phone is there on
 * the desktop.
 *
 * GLOBAL, per ACCOUNT — the store is the BFF (DynamoDB, OWNERPIN# partition),
 * reached over the same session cookie as the rest of /account. This replaced a
 * localStorage set: which agents you watch is now a fact about your account, not
 * about one browser. Read on load, and RE-READ when the account stream (re)opens
 * (Sidebar wires that) — that reconnect/focus tick is what carries a pin made on
 * one device to another. The write pokes the account's watchers server-side, so
 * the tick fires promptly rather than only on the next focus.
 *
 * NO localStorage mirror. A second source of truth beside the server is exactly
 * the drift this change exists to remove; react-query's cache is the only local
 * copy, and it is reconciled against the server, never authoritative over it.
 *
 * WHAT A PIN REMEMBERS: enough to draw the row when the agent is NOT in the
 * roster (session, machine). A pinned agent whose machine is off, or that was
 * ended, must still show — greyed, saying why, with its unpin — because a pin
 * that silently vanishes when the agent does is indistinguishable from never
 * having pinned it, and the moment an agent goes away is exactly when someone
 * watching it wants to know.
 *
 * TAGS — owner, 2026-10-12: 當使用者按下 Pin 的那個按鈕的時候 … 就會彈出一個視窗,
 * Default 就是 Pinned 的那一個 Tag, 但是使用者可以新增另外一種不同的 Tag, 之後就可以輕易
 * 地選擇不同的 Tag 加到某些 Agent 裡面去 … 一群 Agent 專門用來做網頁, 一群 Agent 做公司的
 * project. One "Pinned" section became one section per tag, and he chose ONE TAG PER
 * AGENT (asked that day): picking another tag moves the pin. So a tag is a field on
 * the pin, the account's tags travel beside the pins in the same answer, and both
 * live under the one query key — a write to either is one re-read of both.
 */
const KEY = ['account-pins'] as const

/** One heading over a group of pinned agents. */
export type PinTag = { id: string; name: string }

/** The tag every account has. It can be renamed and cannot be removed; a pin that
 *  names no tag is in it — every pin from before tags, and every pin under a tag
 *  that has since been removed. (agent_pins.py says the same word.) */
export const FIRST_TAG = 'pinned'
const FIRST: PinTag = { id: FIRST_TAG, name: 'Pinned' }

export type Pin = {
  /** The paired machine's id in the account — `device_id` on the stamped roster
   *  row. '' when the roster carried none (a single-machine account). Account-
   *  global, so a pin names the same agent on every device that views it. */
  device: string | null
  /** Channel hash: stable across restarts, derived from the name. What the rail's
   *  "which row is open" check identifies an agent by, together with `device`. */
  hash: string
  /** For the row when the agent is not in the roster to be read from. */
  session: string
  /** The account's name for the machine, same purpose. */
  machine: string | null
  /** Where it sits in the person's vertical order — server-assigned, sparse.
   *  Absent on pins from before ordering existed; they sort after ranked ones. */
  rank?: number
  /** The tag it is filed under; absent = the first one. Read it through `tagOf`. */
  tag?: string
}

/** What it takes to pin an agent: who it is, and enough to draw it when it is away.
 *  Made from a roster row or from a stored pin — the sheet is opened from both. */
export type PinTarget = { device: string; hash: string; session: string; machine: string }

export const targetOfAgent = (a: AgentRow): PinTarget => ({
  device: a.device_id ?? '',
  hash: a.hash,
  session: a.session,
  machine: a.device_name ?? '',
})

export const targetOfPin = (p: Pin): PinTarget => ({
  device: p.device ?? '',
  hash: p.hash,
  session: p.session,
  machine: p.machine ?? '',
})

/** Which tag a pin is under. */
export const tagOf = (p: Pin): string => p.tag ?? FIRST_TAG

/**
 * ONE identity for "is this row that pin" — the same pair the open-row highlight
 * uses (`openAgentHash` + `openAgentDevice`), so a pin can never light up a
 * same-named agent on another machine that the highlight would have told apart.
 *
 * Two arguments rather than a row, because the two callers hold the machine under
 * different names: `device_id` on a roster row, `device` on a stored pin. Both
 * absent-forms ('' and null and undefined) collapse to the same key, so the phone
 * that stored '' and the desktop that reads it agree.
 */
export function pinKey(device: string | null | undefined, hash: string): string {
  return `${device ?? ''}\u0000${hash}`
}

type View = { pins: Pin[]; tags: PinTag[] }
const NOTHING: View = { pins: [], tags: [FIRST] }

/**
 * The server's answer, as the rail holds it: the pins in the person's order, the
 * tags with the first one always there, and no pin naming a tag that is not in the
 * list. The server already answers that way; this is what keeps it true for an
 * answer from a BFF that has never heard of tags (no `tags` at all — one section,
 * as before), and it costs one pass.
 */
function settle(a: PinsAnswer): View {
  const sent = (a.tags ?? []).filter((t) => t.id && t.name)
  const tags = sent.some((t) => t.id === FIRST_TAG) ? sent : [FIRST, ...sent]
  const known = new Set(tags.map((t) => t.id))
  const pins = (a.pins as Pin[]).map((p) => {
    if (p.tag === undefined || (p.tag !== FIRST_TAG && known.has(p.tag))) return p
    const { tag: _gone, ...rest } = p
    return rest
  })
  return { pins: sortPins(pins), tags }
}

/*
 * The cache, for the writes below. Each reads it AS IT IS WHEN CALLED, not the list a
 * render closed over: two pins pressed before a re-render used to be built on the
 * same stale list, and the second would un-draw the first until the server answered.
 */
const held = (qc: QueryClient): View => qc.getQueryData<View>(KEY) ?? NOTHING
const draw = (qc: QueryClient, next: View) => void qc.setQueryData(KEY, next)
const reconcile = (qc: QueryClient) => void qc.invalidateQueries({ queryKey: KEY })
/** A write that answered with the pins and tags as they now are: draw THAT. */
const land = (qc: QueryClient, a: PinsAnswer) => draw(qc, settle(a))

export function usePins() {
  const qc = useQueryClient()
  const { data } = useQuery({
    queryKey: KEY,
    queryFn: async () => settle(await getPins()),
    // The rail already refetches this on the account stream opening (Sidebar), so
    // it is never stale for long; keep the cached copy usable in the meantime.
    staleTime: 30_000,
  })
  const pins: Pin[] = data?.pins ?? NOTHING.pins
  const tags: PinTag[] = data?.tags ?? NOTHING.tags

  const isPinned = useCallback(
    (a: { device_id?: string | undefined; hash: string }) => {
      const k = pinKey(a.device_id, a.hash)
      return pins.some((p) => pinKey(p.device, p.hash) === k)
    },
    [pins],
  )

  /**
   * Pin an agent under a tag, or move a pinned one there.
   *
   * Optimistic: the cache flips now so the row lands the instant the tag is picked,
   * then the server is told, then the two are reconciled. On failure the reconcile
   * pulls the true set back — a pin that did not persist un-draws itself rather
   * than lying. Lands at the BOTTOM of the tag it joins (the server's rule, done
   * here first); placed again where it already is, it keeps its place.
   */
  const place = useCallback(
    (t: PinTarget, tagId: string) => {
      const { pins: cur, tags: curTags } = held(qc)
      const k = pinKey(t.device, t.hash)
      const was = cur.find((p) => pinKey(p.device, p.hash) === k)
      const stays = was !== undefined && tagOf(was) === tagId
      const bottom = Math.max(0, ...cur.map((p) => p.rank ?? 0)) + 1024
      const rank = stays ? was.rank : bottom
      const next: Pin = {
        ...t,
        ...(rank !== undefined ? { rank } : {}),
        ...(tagId !== FIRST_TAG ? { tag: tagId } : {}),
      }
      draw(qc, { tags: curTags, pins: sortPins([...cur.filter((p) => pinKey(p.device, p.hash) !== k), next]) })
      void placePin({ ...t, tag: tagId })
        /*
         * A SERVER FROM BEFORE TAGS has no such path (404, and no refusal code of its
         * own). Pinning is older than tags and must not stop working there — the web
         * bundle and the BFF are released separately, and either can be rolled back
         * alone. Under the first tag, the old route means exactly the same thing.
         */
        .catch((e: unknown) =>
          tagId === FIRST_TAG && e instanceof BffError && e.status === 404 && e.code !== 'unknown_tag'
            ? addPin(t)
            : undefined,
        )
        .catch(() => {})
        .finally(() => reconcile(qc))
    },
    [qc],
  )

  /**
   * Pin an agent under a tag that does not exist yet — one request makes the tag
   * and files the agent, and answers with the result, which is what gets drawn.
   *
   * NOT optimistic, unlike everything else here: the tag's id is the server's to
   * give, and the server may refuse the name. The sheet that asked is still open,
   * showing that it is working, so there is nothing to draw early — and a refusal
   * is thrown to it, to be said where the name was typed.
   */
  const placeNew = useCallback(
    async (t: PinTarget, name: string) => land(qc, await placePin({ ...t, tag_name: name })),
    [qc],
  )

  /** Unpin. By (device, hash) so the greyed row of an agent no longer in the
   *  roster can be unpinned too — there is no `AgentRow` for it. */
  const unpin = useCallback(
    (t: { device: string | null; hash: string }) => {
      const { pins: cur, tags: curTags } = held(qc)
      const k = pinKey(t.device, t.hash)
      draw(qc, { tags: curTags, pins: cur.filter((p) => pinKey(p.device, p.hash) !== k) })
      void removePin({ device: t.device ?? '', hash: t.hash })
        .catch(() => {})
        .finally(() => reconcile(qc))
    },
    [qc],
  )

  /**
   * Within ONE tag's rows, drop the pin at `fromIndex` so it lands at `toIndex` —
   * the vertical drag. (Moving to another tag is the sheet's job, not a drag's.)
   *
   * ONE write per drag: the moved pin takes the midpoint of its new
   * neighbours' ranks, so two devices dragging different rows cannot clobber
   * each other. Only when a neighbour predates ranks (or a gap is exhausted)
   * is the tag's list renumbered — the rare case, paid only when hit.
   * Optimistic like `place`: the row lands the instant it is dropped, and the
   * reconcile pulls the server's truth back over any lie.
   *
   * Ranks are one scale across the whole account and are only ever COMPARED
   * within a tag, so a midpoint between two neighbours of the same tag is all
   * a drop needs — what the other tags' rows hold does not enter into it.
   */
  const move = useCallback(
    (tagId: string, fromIndex: number, toIndex: number) => {
      const { pins: cur, tags: curTags } = held(qc)
      const group = cur.filter((p) => tagOf(p) === tagId)
      if (fromIndex === toIndex || fromIndex < 0 || fromIndex >= group.length) return
      const to = Math.max(0, Math.min(group.length - 1, toIndex))
      const next = [...group]
      const [moved] = next.splice(fromIndex, 1)
      next.splice(to, 0, moved!)
      const reranked = (ranks: Map<string, number>): View => ({
        tags: curTags,
        pins: sortPins(
          cur.map((p) => {
            const r = ranks.get(pinKey(p.device, p.hash))
            return r === undefined ? p : { ...p, rank: r }
          }),
        ),
      })
      const prev = next[to - 1]?.rank
      const after = next[to + 1]?.rank
      const mid = rankBetween(prev, after)
      if (mid !== null && (to === 0 || prev !== undefined) && (to === next.length - 1 || after !== undefined)) {
        draw(qc, reranked(new Map([[pinKey(moved!.device, moved!.hash), mid]])))
        void reorderPin({ device: moved!.device ?? '', hash: moved!.hash, rank: mid })
          .catch(() => {})
          .finally(() => reconcile(qc))
        return
      }
      // A neighbour has no rank yet: renumber this tag's order once.
      const ranks = renumber(next.length)
      draw(qc, reranked(new Map(next.map((p, i) => [pinKey(p.device, p.hash), ranks[i]!]))))
      void Promise.allSettled(
        next.map((p, i) => reorderPin({ device: p.device ?? '', hash: p.hash, rank: ranks[i]! })),
      ).finally(() => reconcile(qc))
    },
    [qc],
  )

  /**
   * The drag, by WHO rather than by where: put `src` where `dst` stands. Both are
   * found in the cache as it is at the drop — a drag is held for seconds, and
   * another device may have pinned, unpinned or moved a row of this tag meanwhile,
   * so an index counted when the finger went down can name a different pin by the
   * time it lifts. A pin that is gone, or no longer under the same tag, moves nothing.
   */
  const moveTo = useCallback(
    (src: { device: string | null; hash: string }, dst: { device: string | null; hash: string }) => {
      const cur = held(qc).pins
      const key = (x: { device: string | null; hash: string }) => pinKey(x.device, x.hash)
      const me = cur.find((p) => key(p) === key(src))
      if (!me) return
      const group = cur.filter((p) => tagOf(p) === tagOf(me))
      const from = group.findIndex((p) => key(p) === key(src))
      const to = group.findIndex((p) => key(p) === key(dst))
      if (to >= 0) move(tagOf(me), from, to)
    },
    [qc, move],
  )

  /** Call a tag something else. Awaited, not optimistic: the server refuses a name
   *  another tag has, and says so — thrown to the sheet, which shows it. */
  const renameTag = useCallback(
    async (id: string, name: string) => land(qc, await renamePinTag({ id, name })),
    [qc],
  )

  /** Remove a tag. Its agents stay pinned — the server moves them under the first
   *  tag and answers with the result. */
  const removeTag = useCallback(
    async (id: string) => land(qc, await removePinTag(id)),
    [qc],
  )

  return { pins, tags, isPinned, place, placeNew, unpin, move, moveTo, renameTag, removeTag }
}
